import type {
  APIAuditLog,
  APIAutoModerationRule,
  APIBan,
  APIChannel,
  APIEmoji,
  APIGuild,
  APIGuildMember,
  APIGuildScheduledEvent,
  APIGuildStatistics,
  APIInvite,
  APIMemberVerification,
  APIMessage,
  APIPartialGuild,
  APIPinnedMessage,
  APIRole,
  APISearchResults,
  APISticker,
  APIThreadMember,
  APIUser,
  APIUserProfile,
  APIUserNote,
  APIUserGuildSettings,
  APIUserSettings,
  APIWelcomeScreen,
} from '../contracts/chat-types.js';
import type { components } from '../contracts/generated/openapi.js';
import type {
  AuthTokens,
  HttpClient,
  RequestOptions,
  UploadFile,
} from './http.js';

type Schemas = components['schemas'];
type Snowflake = string;
type Reason = { reason?: string | null };

export type LoginResponse = Schemas['AuthLoginResponseDto'];

/** Ação pendente da conta: trocar a senha inicial. */
export const REQUIRED_ACTION_UPDATE_PASSWORD = 'UPDATE_PASSWORD';

export type FirstAccessPreview = {
  account_id: number;
  name: string;
  username: string;
};

/** Cópia dos dados (o `harvest` do Discord). */
/** `GET /chat/client-versions`: o que o painel define para os apps. */
export type ClientVersions = {
  min_versions: {
    web: string | null;
    desktop: string | null;
    android: string | null;
    ios: string | null;
  };
  desktop_channel: 'estavel' | 'beta';
  updated_at: string | null;
};

/** Preferências do push no celular (por conta). */
export type PushSettings = {
  /** Só manda push se a pessoa estiver parada no computador/navegador. */
  inactive_only: boolean;
  /** Parada há quantos minutos (1 a 60). */
  inactive_minutes: number;
  /** Notificação com som. */
  sound: boolean;
};

export type DataExport = {
  harvest_id: string;
  user_id: string;
  status: 'pending' | 'ready' | 'failed' | 'expired';
  created_at: string;
  completed_at: string | null;
  expires_at: string | null;
  size_bytes: number | null;
};
export type CreateMessageBody = Schemas['CreateMessageDto'];
export type EditMessageBody = Schemas['EditMessageDto'];
export type ModifyChannelBody = Schemas['ModifyChannelDto'];
export type CreateGuildChannelBody = Schemas['CreateGuildChannelDto'];
export type StartThreadBody = Schemas['StartThreadDto'];
export type StartThreadFromMessageBody = Schemas['StartThreadFromMessageDto'];
export type RoleBody = Schemas['RoleDto'];
export type ModifyMemberBody = Schemas['ModifyMemberDto'];
export type CreateGuildBody = Schemas['CreateGuildDto'];
export type ModifyGuildBody = Schemas['ModifyGuildDto'];
export type CreateInviteBody = Schemas['CreateInviteDto'];
export type CreateEmojiBody = Schemas['CreateEmojiDto'];
export type ModifyEmojiBody = Schemas['ModifyEmojiDto'];
export type StickerBody = Schemas['StickerDto'];
export type ModifyStickerBody = Schemas['ModifyStickerDto'];
export type CreateScheduledEventBody = Schemas['CreateScheduledEventDto'];
export type ModifyScheduledEventBody = Schemas['ModifyScheduledEventDto'];
export type CreateAutomodRuleBody = Schemas['CreateAutomodRuleDto'];
export type ModifyAutomodRuleBody = Schemas['ModifyAutomodRuleDto'];
export type UserSettingsBody = Schemas['UserSettingsDto'];
export type GuildSettingsBody = Schemas['GuildSettingsDto'];
export type ModifyWelcomeScreenBody = Schemas['ModifyWelcomeScreenDto'];
export type ModifyMemberVerificationBody =
  Schemas['ModifyMemberVerificationDto'];
export type JoinRequestBody = Schemas['JoinRequestDto'];
export type PutOverwriteBody = Schemas['PutOverwriteDto'];

export type MessagesQuery = {
  limit?: number;
  before?: Snowflake;
  after?: Snowflake;
  around?: Snowflake;
};

export type SearchQuery = {
  content?: string;
  author_id?: Snowflake[];
  author_type?: string[];
  mentions?: Snowflake[];
  mention_everyone?: boolean;
  channel_id?: Snowflake[];
  has?: string[];
  pinned?: boolean;
  min_id?: Snowflake;
  max_id?: Snowflake;
  sort_by?: 'timestamp' | 'relevance';
  sort_order?: 'asc' | 'desc';
  offset?: number;
  limit?: number;
};

export type ThreadList = {
  threads: APIChannel[];
  members: APIThreadMember[];
  has_more?: boolean;
};

/** A conta de login da base (`GET /me`). */
export type Account = {
  id: number;
  name: string;
  username: string | null;
  email: string;
  status: string | null;
  companyId: number | null;
  createdAt: string;
};

/** Sessão da conta (rota da base `/me/sessions`). */
export type UserSession = {
  id: string;
  deviceLabel: string | null;
  browser: string | null;
  os: string | null;
  deviceType: string | null;
  deviceModel: string | null;
  clientType: 'web' | 'app' | 'unknown';
  appVersion: string | null;
  ipLast: string | null;
  createdAt: string;
  lastSeenAt: string;
  isCurrent: boolean;
};

export type ThreadSearchQuery = {
  name?: string;
  /** Sem: abertas e arquivadas. */
  archived?: boolean;
  sort_by?: 'last_message_time' | 'creation_time' | 'relevance';
  sort_order?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  tag?: Snowflake[];
  tag_setting?: 'match_some' | 'match_all';
};

export type ThreadSearchResult = ThreadList & {
  /** A primeira mensagem de cada post (o ID é o da thread). */
  first_messages: APIMessage[];
  total_results: number;
};

/** Emoji na URL de reação: Unicode como está, personalizado como `nome:id`. */
export function reactionEmojiParam(emoji: {
  id?: Snowflake | null;
  name?: string | null;
}): string {
  return encodeURIComponent(
    emoji.id ? `${emoji.name ?? '_'}:${emoji.id}` : (emoji.name ?? ''),
  );
}

const enc = encodeURIComponent;

/**
 * As rotas do chat (`/api/chat/...`), com os caminhos e formatos da API v10
 * do Discord. Cada método é uma rota; quem decide permissão é a API.
 */
export class ChatApi {
  constructor(private readonly http: HttpClient) {}

  private get<T>(path: string, options?: RequestOptions) {
    return this.http.request<T>('GET', `/chat${path}`, options);
  }
  private post<T>(path: string, body?: unknown, options?: RequestOptions) {
    return this.http.request<T>('POST', `/chat${path}`, { ...options, body });
  }
  private patch<T>(path: string, body?: unknown, options?: RequestOptions) {
    return this.http.request<T>('PATCH', `/chat${path}`, { ...options, body });
  }
  private put<T>(path: string, body?: unknown, options?: RequestOptions) {
    return this.http.request<T>('PUT', `/chat${path}`, { ...options, body });
  }
  private del<T>(path: string, options?: RequestOptions) {
    return this.http.request<T>('DELETE', `/chat${path}`, options);
  }

  // -------------------------------------------------------------------------
  // Sessão
  // -------------------------------------------------------------------------

  /**
   * Login por e-mail ou usuário (`login`, como o Discord); no `app`, os
   * tokens vêm no corpo (guarde no TokenStore). `requiredActions` com
   * `UPDATE_PASSWORD`: senha inicial, trocar antes de usar o chat.
   */
  login(login: string, password: string): Promise<LoginResponse> {
    return this.http.request<LoginResponse>('POST', '/auth/login', {
      body: { login, password },
    });
  }

  /** Quem é o dono do link de primeiro acesso (antes de pedir a senha). */
  firstAccess(token: string): Promise<FirstAccessPreview> {
    return this.http.request('GET', `/auth/first-access/${enc(token)}`);
  }

  /** Define a senha pelo link de primeiro acesso; a resposta é a do login. */
  completeFirstAccess(token: string, password: string): Promise<LoginResponse> {
    return this.http.request('POST', `/auth/first-access/${enc(token)}`, {
      body: { password },
    });
  }

  logout(tokens?: AuthTokens | null): Promise<{ loggedOut: boolean }> {
    return this.http.request('POST', '/auth/logout', {
      body: tokens?.refreshToken ? { refreshToken: tokens.refreshToken } : {},
    });
  }

  /** Versões mínimas por plataforma e o canal do desktop (rota pública, o painel define). */
  clientVersions(): Promise<ClientVersions> {
    return this.get('/client-versions');
  }

  /** `GET /chat/gateway`: a URL do WebSocket. */
  gateway(): Promise<{ url: string }> {
    return this.get('/gateway');
  }

  // -------------------------------------------------------------------------
  // Eu
  // -------------------------------------------------------------------------

  /** Qualquer pessoa pelo ID (quem saiu do servidor, por exemplo). */
  user(userId: Snowflake): Promise<APIUser> {
    return this.get(`/users/${userId}`);
  }
  /** `required_actions` (nosso): `UPDATE_PASSWORD` enquanto a senha inicial não muda. */
  me(): Promise<
    APIUser & { bio?: string | null; required_actions?: string[] }
  > {
    return this.get('/users/@me');
  }
  /** Troca a senha com a atual; as outras sessões caem (como o Discord). */
  changePassword(
    password: string,
    newPassword: string,
  ): Promise<APIUser & { required_actions?: string[] }> {
    return this.patch('/users/@me', { password, new_password: newPassword });
  }
  /** Troca o nome de usuário (vale também para entrar), com a senha. */
  changeUsername(username: string, password: string): Promise<APIUser> {
    return this.patch('/users/@me', { username, password });
  }
  /** Último pedido de cópia dos dados (LGPD), ou `null`. */
  async latestDataExport(): Promise<DataExport | null> {
    const found = await this.get<Partial<DataExport> | null>(
      '/users/@me/harvest',
    );
    return found?.harvest_id ? (found as DataExport) : null;
  }
  requestDataExport(): Promise<DataExport> {
    return this.post('/users/@me/harvest');
  }
  /** Caminho do zip, relativo à base da API (o navegador baixa com o cookie). */
  dataExportPath(harvestId: string): string {
    return `/chat/users/@me/harvest/${enc(harvestId)}/download`;
  }
  /** Aparelho para push (celular), preso à sessão. */
  registerPushDevice(provider: 'fcm' | 'apns', token: string): Promise<void> {
    return this.post('/users/@me/devices', { provider, token });
  }
  unregisterPushDevice(provider: 'fcm' | 'apns', token: string): Promise<void> {
    return this.http.request('DELETE', '/chat/users/@me/devices', {
      body: { provider, token },
    });
  }
  /**
   * Preferências do push (nossas): só quando a pessoa está parada nos
   * outros aparelhos (e depois de quantos minutos) e o som.
   */
  pushSettings(): Promise<PushSettings> {
    return this.get('/users/@me/push-settings');
  }
  updatePushSettings(body: Partial<PushSettings>): Promise<PushSettings> {
    return this.patch('/users/@me/push-settings', body);
  }
  /** Perfil: nome de exibição, avatar e faixa (data URI ou nulo), cor, "sobre mim". */
  modifyMe(body: {
    global_name?: string | null;
    avatar?: string | null;
    banner?: string | null;
    accent_color?: number | null;
    bio?: string | null;
  }): Promise<APIUser & { bio: string | null }> {
    return this.patch('/users/@me', body);
  }
  /** Perfil completo (como o Discord): sobre mim, faixa, membro, servidores em comum. */
  profile(
    userId: Snowflake,
    guildId?: Snowflake | null,
  ): Promise<APIUserProfile> {
    return this.get(`/users/${userId}/profile`, {
      query: { guild_id: guildId ?? undefined },
    });
  }
  /** A conta de login (rota da base `/me`): e-mail, usuário, empresa. */
  account(): Promise<Account> {
    return this.http.request('GET', '/me');
  }
  /** Onde a conta está conectada (rota da base, fora do `/chat`). */
  mySessions(): Promise<UserSession[]> {
    return this.http.request('GET', '/me/sessions');
  }
  revokeSession(sessionId: string): Promise<unknown> {
    return this.http.request('DELETE', `/me/sessions/${enc(sessionId)}`);
  }
  revokeOtherSessions(): Promise<{ revokedCount: number }> {
    return this.http.request('POST', '/me/sessions/revoke-others');
  }
  myGuilds(): Promise<APIPartialGuild[]> {
    return this.get('/users/@me/guilds');
  }
  myMember(guildId: Snowflake): Promise<APIGuildMember> {
    return this.get(`/users/@me/guilds/${guildId}/member`);
  }
  leaveGuild(guildId: Snowflake): Promise<void> {
    return this.del(`/users/@me/guilds/${guildId}`);
  }
  privateChannels(): Promise<APIChannel[]> {
    return this.get('/users/@me/channels');
  }
  /** DM com uma pessoa (`recipientId`) ou grupo (`recipients`). */
  openPrivateChannel(
    body: { recipient_id: Snowflake } | { recipients: Snowflake[] },
  ): Promise<APIChannel> {
    return this.post('/users/@me/channels', body);
  }
  userSettings(): Promise<APIUserSettings> {
    return this.get('/users/@me/settings');
  }
  updateUserSettings(body: UserSettingsBody): Promise<APIUserSettings> {
    return this.patch('/users/@me/settings', body);
  }
  /** `@me` = as DMs. */
  guildSettings(guildId: Snowflake | '@me'): Promise<APIUserGuildSettings> {
    return this.get(`/users/@me/guilds/${guildId}/settings`);
  }
  updateGuildSettings(
    guildId: Snowflake | '@me',
    body: GuildSettingsBody,
  ): Promise<APIUserGuildSettings> {
    return this.patch(`/users/@me/guilds/${guildId}/settings`, body);
  }

  // -------------------------------------------------------------------------
  // Servidor
  // -------------------------------------------------------------------------

  guild(guildId: Snowflake): Promise<APIGuild> {
    return this.get(`/guilds/${guildId}`);
  }
  createGuild(body: CreateGuildBody): Promise<APIGuild> {
    return this.post('/guilds', body);
  }
  modifyGuild(
    guildId: Snowflake,
    body: ModifyGuildBody,
    options: Reason = {},
  ): Promise<APIGuild> {
    return this.patch(`/guilds/${guildId}`, body, {
      auditReason: options.reason,
    });
  }
  deleteGuild(guildId: Snowflake): Promise<void> {
    return this.del(`/guilds/${guildId}`);
  }
  guildChannels(guildId: Snowflake): Promise<APIChannel[]> {
    return this.get(`/guilds/${guildId}/channels`);
  }
  createChannel(
    guildId: Snowflake,
    body: CreateGuildChannelBody,
    options: Reason = {},
  ): Promise<APIChannel> {
    return this.post(`/guilds/${guildId}/channels`, body, {
      auditReason: options.reason,
    });
  }
  reorderChannels(
    guildId: Snowflake,
    items: Array<{
      id: Snowflake;
      position?: number | null;
      parent_id?: Snowflake | null;
      lock_permissions?: boolean;
    }>,
  ): Promise<void> {
    return this.patch(`/guilds/${guildId}/channels`, items);
  }
  roles(guildId: Snowflake): Promise<APIRole[]> {
    return this.get(`/guilds/${guildId}/roles`);
  }
  createRole(
    guildId: Snowflake,
    body: RoleBody,
    options: Reason = {},
  ): Promise<APIRole> {
    return this.post(`/guilds/${guildId}/roles`, body, {
      auditReason: options.reason,
    });
  }
  modifyRole(
    guildId: Snowflake,
    roleId: Snowflake,
    body: RoleBody,
    options: Reason = {},
  ): Promise<APIRole> {
    return this.patch(`/guilds/${guildId}/roles/${roleId}`, body, {
      auditReason: options.reason,
    });
  }
  deleteRole(
    guildId: Snowflake,
    roleId: Snowflake,
    options: Reason = {},
  ): Promise<void> {
    return this.del(`/guilds/${guildId}/roles/${roleId}`, {
      auditReason: options.reason,
    });
  }
  reorderRoles(
    guildId: Snowflake,
    items: Array<{ id: Snowflake; position?: number | null }>,
  ): Promise<APIRole[]> {
    return this.patch(`/guilds/${guildId}/roles`, items);
  }
  activeThreads(guildId: Snowflake): Promise<ThreadList> {
    return this.get(`/guilds/${guildId}/threads/active`);
  }

  // --- Membros e banimentos -------------------------------------------------

  members(
    guildId: Snowflake,
    query: { limit?: number; after?: Snowflake } = {},
  ): Promise<APIGuildMember[]> {
    return this.get(`/guilds/${guildId}/members`, { query });
  }
  member(guildId: Snowflake, userId: Snowflake): Promise<APIGuildMember> {
    return this.get(`/guilds/${guildId}/members/${userId}`);
  }
  searchMembers(
    guildId: Snowflake,
    query: string,
    limit = 10,
  ): Promise<APIGuildMember[]> {
    return this.get(`/guilds/${guildId}/members/search`, {
      query: { query, limit },
    });
  }
  modifyMember(
    guildId: Snowflake,
    userId: Snowflake,
    body: ModifyMemberBody,
    options: Reason = {},
  ): Promise<APIGuildMember> {
    return this.patch(`/guilds/${guildId}/members/${userId}`, body, {
      auditReason: options.reason,
    });
  }
  modifyMyMember(
    guildId: Snowflake,
    body: { nick?: string | null; avatar?: string | null },
  ): Promise<APIGuildMember> {
    return this.patch(`/guilds/${guildId}/members/@me`, body);
  }
  addMemberRole(
    guildId: Snowflake,
    userId: Snowflake,
    roleId: Snowflake,
    options: Reason = {},
  ): Promise<void> {
    return this.put(
      `/guilds/${guildId}/members/${userId}/roles/${roleId}`,
      undefined,
      { auditReason: options.reason },
    );
  }
  removeMemberRole(
    guildId: Snowflake,
    userId: Snowflake,
    roleId: Snowflake,
    options: Reason = {},
  ): Promise<void> {
    return this.del(`/guilds/${guildId}/members/${userId}/roles/${roleId}`, {
      auditReason: options.reason,
    });
  }
  kick(guildId: Snowflake, userId: Snowflake, options: Reason = {}) {
    return this.del<void>(`/guilds/${guildId}/members/${userId}`, {
      auditReason: options.reason,
    });
  }
  bans(
    guildId: Snowflake,
    query: { limit?: number; before?: Snowflake; after?: Snowflake } = {},
  ): Promise<APIBan[]> {
    return this.get(`/guilds/${guildId}/bans`, { query });
  }
  ban(guildId: Snowflake, userId: Snowflake): Promise<APIBan> {
    return this.get(`/guilds/${guildId}/bans/${userId}`);
  }
  createBan(
    guildId: Snowflake,
    userId: Snowflake,
    body: { delete_message_seconds?: number } = {},
    options: Reason = {},
  ): Promise<void> {
    return this.put(`/guilds/${guildId}/bans/${userId}`, body, {
      auditReason: options.reason,
    });
  }
  removeBan(guildId: Snowflake, userId: Snowflake, options: Reason = {}) {
    return this.del<void>(`/guilds/${guildId}/bans/${userId}`, {
      auditReason: options.reason,
    });
  }
  bulkBan(
    guildId: Snowflake,
    body: { user_ids: Snowflake[]; delete_message_seconds?: number },
    options: Reason = {},
  ): Promise<{ banned_users: Snowflake[]; failed_users: Snowflake[] }> {
    return this.post(`/guilds/${guildId}/bulk-ban`, body, {
      auditReason: options.reason,
    });
  }

  // --- Boas-vindas, regras, auditoria, estatísticas -------------------------

  welcomeScreen(guildId: Snowflake): Promise<APIWelcomeScreen> {
    return this.get(`/guilds/${guildId}/welcome-screen`);
  }
  modifyWelcomeScreen(
    guildId: Snowflake,
    body: ModifyWelcomeScreenBody,
  ): Promise<APIWelcomeScreen> {
    return this.patch(`/guilds/${guildId}/welcome-screen`, body);
  }
  memberVerification(guildId: Snowflake): Promise<APIMemberVerification> {
    return this.get(`/guilds/${guildId}/member-verification`);
  }
  modifyMemberVerification(
    guildId: Snowflake,
    body: ModifyMemberVerificationBody,
  ): Promise<APIMemberVerification> {
    return this.patch(`/guilds/${guildId}/member-verification`, body);
  }
  /** Aceita as regras do servidor (deixa de ser pendente). */
  acceptRules(guildId: Snowflake, body: JoinRequestBody) {
    return this.put<Record<string, unknown>>(
      `/guilds/${guildId}/requests/@me`,
      body,
    );
  }
  auditLog(
    guildId: Snowflake,
    query: {
      user_id?: Snowflake;
      action_type?: number;
      before?: Snowflake;
      after?: Snowflake;
      limit?: number;
    } = {},
  ): Promise<APIAuditLog> {
    return this.get(`/guilds/${guildId}/audit-logs`, { query });
  }
  statistics(guildId: Snowflake, days = 14): Promise<APIGuildStatistics> {
    return this.get(`/guilds/${guildId}/statistics`, { query: { days } });
  }

  // --- Convites -------------------------------------------------------------

  guildInvites(guildId: Snowflake): Promise<APIInvite[]> {
    return this.get(`/guilds/${guildId}/invites`);
  }
  channelInvites(channelId: Snowflake): Promise<APIInvite[]> {
    return this.get(`/channels/${channelId}/invites`);
  }
  createInvite(
    channelId: Snowflake,
    body: CreateInviteBody = {},
    options: Reason = {},
  ): Promise<APIInvite> {
    return this.post(`/channels/${channelId}/invites`, body, {
      auditReason: options.reason,
    });
  }
  invite(code: string, withCounts = false): Promise<APIInvite> {
    return this.get(`/invites/${enc(code)}`, {
      query: { with_counts: withCounts || undefined },
    });
  }
  acceptInvite(code: string): Promise<APIInvite> {
    return this.post(`/invites/${enc(code)}`);
  }
  deleteInvite(code: string, options: Reason = {}): Promise<APIInvite> {
    return this.del(`/invites/${enc(code)}`, { auditReason: options.reason });
  }

  // --- Emojis e figurinhas ----------------------------------------------------

  emojis(guildId: Snowflake): Promise<APIEmoji[]> {
    return this.get(`/guilds/${guildId}/emojis`);
  }
  createEmoji(
    guildId: Snowflake,
    body: CreateEmojiBody,
    options: Reason = {},
  ): Promise<APIEmoji> {
    return this.post(`/guilds/${guildId}/emojis`, body, {
      auditReason: options.reason,
    });
  }
  modifyEmoji(
    guildId: Snowflake,
    emojiId: Snowflake,
    body: ModifyEmojiBody,
    options: Reason = {},
  ): Promise<APIEmoji> {
    return this.patch(`/guilds/${guildId}/emojis/${emojiId}`, body, {
      auditReason: options.reason,
    });
  }
  deleteEmoji(guildId: Snowflake, emojiId: Snowflake, options: Reason = {}) {
    return this.del<void>(`/guilds/${guildId}/emojis/${emojiId}`, {
      auditReason: options.reason,
    });
  }
  stickers(guildId: Snowflake): Promise<APISticker[]> {
    return this.get(`/guilds/${guildId}/stickers`);
  }
  sticker(stickerId: Snowflake): Promise<APISticker> {
    return this.get(`/stickers/${stickerId}`);
  }
  /** Multipart como o Discord: `file` + `name`, `description`, `tags`. */
  createSticker(
    guildId: Snowflake,
    body: StickerBody,
    file: UploadFile,
    options: Reason = {},
  ): Promise<APISticker> {
    const form = new FormData();
    form.append('name', body.name);
    form.append('tags', body.tags);
    if (body.description) form.append('description', body.description);
    if (file.data instanceof Blob) form.append('file', file.data, file.name);
    else
      form.append('file', { ...file.data, name: file.name } as unknown as Blob);
    return this.http.request('POST', `/chat/guilds/${guildId}/stickers`, {
      body: form,
      auditReason: options.reason,
    });
  }
  modifySticker(
    guildId: Snowflake,
    stickerId: Snowflake,
    body: ModifyStickerBody,
    options: Reason = {},
  ): Promise<APISticker> {
    return this.patch(`/guilds/${guildId}/stickers/${stickerId}`, body, {
      auditReason: options.reason,
    });
  }
  deleteSticker(
    guildId: Snowflake,
    stickerId: Snowflake,
    options: Reason = {},
  ) {
    return this.del<void>(`/guilds/${guildId}/stickers/${stickerId}`, {
      auditReason: options.reason,
    });
  }

  // --- Eventos ----------------------------------------------------------------

  scheduledEvents(
    guildId: Snowflake,
    withUserCount = false,
  ): Promise<APIGuildScheduledEvent[]> {
    return this.get(`/guilds/${guildId}/scheduled-events`, {
      query: { with_user_count: withUserCount || undefined },
    });
  }
  createScheduledEvent(
    guildId: Snowflake,
    body: CreateScheduledEventBody,
    options: Reason = {},
  ): Promise<APIGuildScheduledEvent> {
    return this.post(`/guilds/${guildId}/scheduled-events`, body, {
      auditReason: options.reason,
    });
  }
  modifyScheduledEvent(
    guildId: Snowflake,
    eventId: Snowflake,
    body: ModifyScheduledEventBody,
    options: Reason = {},
  ): Promise<APIGuildScheduledEvent> {
    return this.patch(`/guilds/${guildId}/scheduled-events/${eventId}`, body, {
      auditReason: options.reason,
    });
  }
  deleteScheduledEvent(
    guildId: Snowflake,
    eventId: Snowflake,
    options: Reason = {},
  ) {
    return this.del<void>(`/guilds/${guildId}/scheduled-events/${eventId}`, {
      auditReason: options.reason,
    });
  }
  scheduledEventUsers(
    guildId: Snowflake,
    eventId: Snowflake,
    query: { limit?: number; with_member?: boolean } = {},
  ): Promise<
    Array<{
      guild_scheduled_event_id: Snowflake;
      user: APIUser;
      member?: Omit<APIGuildMember, 'user'>;
    }>
  > {
    return this.get(`/guilds/${guildId}/scheduled-events/${eventId}/users`, {
      query,
    });
  }
  /** Eventos em que a pessoa marcou interesse. */
  myScheduledEvents(guildIds?: Snowflake[]): Promise<
    Array<{
      guild_scheduled_event_id: Snowflake;
      guild_id: Snowflake;
      user_id: Snowflake;
    }>
  > {
    return this.get('/users/@me/scheduled-events', {
      query: { guild_ids: guildIds?.length ? guildIds.join(',') : undefined },
    });
  }
  setEventInterest(
    guildId: Snowflake,
    eventId: Snowflake,
    interested: boolean,
  ): Promise<void> {
    const path = `/guilds/${guildId}/scheduled-events/${eventId}/users/@me`;
    return interested ? this.put(path) : this.del(path);
  }

  // --- Moderação automática ---------------------------------------------------

  automodRules(guildId: Snowflake): Promise<APIAutoModerationRule[]> {
    return this.get(`/guilds/${guildId}/auto-moderation/rules`);
  }
  automodRule(
    guildId: Snowflake,
    ruleId: Snowflake,
  ): Promise<APIAutoModerationRule> {
    return this.get(`/guilds/${guildId}/auto-moderation/rules/${ruleId}`);
  }
  createAutomodRule(
    guildId: Snowflake,
    body: CreateAutomodRuleBody,
    options: Reason = {},
  ): Promise<APIAutoModerationRule> {
    return this.post(`/guilds/${guildId}/auto-moderation/rules`, body, {
      auditReason: options.reason,
    });
  }
  modifyAutomodRule(
    guildId: Snowflake,
    ruleId: Snowflake,
    body: ModifyAutomodRuleBody,
    options: Reason = {},
  ): Promise<APIAutoModerationRule> {
    return this.patch(
      `/guilds/${guildId}/auto-moderation/rules/${ruleId}`,
      body,
      { auditReason: options.reason },
    );
  }
  deleteAutomodRule(
    guildId: Snowflake,
    ruleId: Snowflake,
    options: Reason = {},
  ) {
    return this.del<void>(
      `/guilds/${guildId}/auto-moderation/rules/${ruleId}`,
      { auditReason: options.reason },
    );
  }

  // --- Caixa de entrada e notas ----------------------------------------------

  /** Menções recentes (à pessoa, aos cargos dela, a todos), a mais nova primeiro. */
  recentMentions(
    query: {
      limit?: number;
      before?: Snowflake;
      guild_id?: Snowflake;
      roles?: boolean;
      everyone?: boolean;
    } = {},
  ): Promise<APIMessage[]> {
    return this.get('/users/@me/mentions', { query });
  }
  notes(): Promise<Record<Snowflake, string>> {
    return this.get('/users/@me/notes');
  }
  note(userId: Snowflake): Promise<APIUserNote> {
    return this.get(`/users/@me/notes/${userId}`);
  }
  /** Vazia apaga. */
  setNote(userId: Snowflake, note: string): Promise<void> {
    return this.put(`/users/@me/notes/${userId}`, { note });
  }

  // --- Busca ------------------------------------------------------------------

  searchGuild(
    guildId: Snowflake,
    query: SearchQuery,
  ): Promise<APISearchResults> {
    return this.get(`/guilds/${guildId}/messages/search`, { query });
  }
  searchChannel(
    channelId: Snowflake,
    query: SearchQuery,
  ): Promise<APISearchResults> {
    return this.get(`/channels/${channelId}/messages/search`, { query });
  }

  // -------------------------------------------------------------------------
  // Canais
  // -------------------------------------------------------------------------

  channel(channelId: Snowflake): Promise<APIChannel> {
    return this.get(`/channels/${channelId}`);
  }
  /** Canal do servidor, thread ou grupo (cada tipo usa os seus campos). */
  modifyChannel(
    channelId: Snowflake,
    body: ModifyChannelBody,
    options: Reason = {},
  ): Promise<APIChannel> {
    return this.patch(`/channels/${channelId}`, body, {
      auditReason: options.reason,
    });
  }
  /** Apaga canal ou thread; fecha DM; sai do grupo. */
  /** Apaga canal ou thread, fecha DM, sai do grupo (`silent`: sem o aviso no grupo). */
  deleteChannel(
    channelId: Snowflake,
    options: Reason & { silent?: boolean } = {},
  ) {
    return this.del<APIChannel | void>(`/channels/${channelId}`, {
      auditReason: options.reason,
      query: options.silent ? { silent: true } : undefined,
    });
  }
  putOverwrite(
    channelId: Snowflake,
    targetId: Snowflake,
    body: PutOverwriteBody,
    options: Reason = {},
  ): Promise<void> {
    return this.put(`/channels/${channelId}/permissions/${targetId}`, body, {
      auditReason: options.reason,
    });
  }
  deleteOverwrite(
    channelId: Snowflake,
    targetId: Snowflake,
    options: Reason = {},
  ) {
    return this.del<void>(`/channels/${channelId}/permissions/${targetId}`, {
      auditReason: options.reason,
    });
  }
  /** Nosso: copia os overwrites da categoria. */
  syncPermissions(channelId: Snowflake): Promise<APIChannel> {
    return this.post(`/channels/${channelId}/sync-permissions`);
  }
  typing(channelId: Snowflake): Promise<void> {
    return this.post(`/channels/${channelId}/typing`);
  }

  // --- Mensagens --------------------------------------------------------------

  /** Mais nova primeiro, como o Discord (também com `after`). */
  messages(
    channelId: Snowflake,
    query: MessagesQuery = {},
  ): Promise<APIMessage[]> {
    return this.get(`/channels/${channelId}/messages`, { query });
  }
  message(channelId: Snowflake, messageId: Snowflake): Promise<APIMessage> {
    return this.get(`/channels/${channelId}/messages/${messageId}`);
  }
  /** Com arquivos, vai em multipart (`payload_json` + `files[n]`). */
  sendMessage(
    channelId: Snowflake,
    body: CreateMessageBody,
    files: UploadFile[] = [],
  ): Promise<APIMessage> {
    return this.http.request('POST', `/chat/channels/${channelId}/messages`, {
      body,
      files,
      // Enviar tem limite próprio: quem decide repetir é a fila de envio.
      retryRateLimit: false,
    });
  }
  editMessage(
    channelId: Snowflake,
    messageId: Snowflake,
    body: EditMessageBody,
  ): Promise<APIMessage> {
    return this.patch(`/channels/${channelId}/messages/${messageId}`, body);
  }
  deleteMessage(
    channelId: Snowflake,
    messageId: Snowflake,
    options: Reason = {},
  ): Promise<void> {
    return this.del(`/channels/${channelId}/messages/${messageId}`, {
      auditReason: options.reason,
    });
  }
  bulkDeleteMessages(
    channelId: Snowflake,
    messages: Snowflake[],
    options: Reason = {},
  ): Promise<void> {
    return this.post(
      `/channels/${channelId}/messages/bulk-delete`,
      { messages },
      { auditReason: options.reason },
    );
  }
  /** Lido até a mensagem; `manual` marca como não lida a partir dela. */
  ack(
    channelId: Snowflake,
    messageId: Snowflake,
    body: { manual?: boolean; mention_count?: number } = {},
  ): Promise<{ token: null }> {
    return this.post(`/channels/${channelId}/messages/${messageId}/ack`, body);
  }

  // --- Reações ----------------------------------------------------------------

  addReaction(
    channelId: Snowflake,
    messageId: Snowflake,
    emoji: { id?: Snowflake | null; name?: string | null },
    burst = false,
  ): Promise<void> {
    return this.put(
      `/channels/${channelId}/messages/${messageId}/reactions/${reactionEmojiParam(emoji)}/@me`,
      undefined,
      { query: { type: burst ? 1 : undefined } },
    );
  }
  removeReaction(
    channelId: Snowflake,
    messageId: Snowflake,
    emoji: { id?: Snowflake | null; name?: string | null },
    options: { userId?: Snowflake; burst?: boolean } = {},
  ): Promise<void> {
    return this.del(
      `/channels/${channelId}/messages/${messageId}/reactions/${reactionEmojiParam(emoji)}/${options.userId ?? '@me'}`,
      { query: { type: options.burst ? 1 : undefined } },
    );
  }
  reactions(
    channelId: Snowflake,
    messageId: Snowflake,
    emoji: { id?: Snowflake | null; name?: string | null },
    query: { type?: 0 | 1; after?: Snowflake; limit?: number } = {},
  ): Promise<APIUser[]> {
    return this.get(
      `/channels/${channelId}/messages/${messageId}/reactions/${reactionEmojiParam(emoji)}`,
      { query },
    );
  }
  removeAllReactions(
    channelId: Snowflake,
    messageId: Snowflake,
    emoji?: { id?: Snowflake | null; name?: string | null },
  ): Promise<void> {
    const suffix = emoji ? `/${reactionEmojiParam(emoji)}` : '';
    return this.del(
      `/channels/${channelId}/messages/${messageId}/reactions${suffix}`,
    );
  }

  // --- Fixadas e enquetes -----------------------------------------------------

  pins(
    channelId: Snowflake,
    query: { before?: string; limit?: number } = {},
  ): Promise<{ items: APIPinnedMessage[]; has_more: boolean }> {
    return this.get(`/channels/${channelId}/messages/pins`, { query });
  }
  pin(channelId: Snowflake, messageId: Snowflake, options: Reason = {}) {
    return this.put<void>(
      `/channels/${channelId}/messages/pins/${messageId}`,
      undefined,
      { auditReason: options.reason },
    );
  }
  unpin(channelId: Snowflake, messageId: Snowflake, options: Reason = {}) {
    return this.del<void>(`/channels/${channelId}/messages/pins/${messageId}`, {
      auditReason: options.reason,
    });
  }
  /** Voto (troca o anterior); lista vazia tira o voto. */
  vote(
    channelId: Snowflake,
    messageId: Snowflake,
    answerIds: number[],
  ): Promise<void> {
    return this.put(`/channels/${channelId}/polls/${messageId}/answers/@me`, {
      answer_ids: answerIds,
    });
  }
  pollVoters(
    channelId: Snowflake,
    messageId: Snowflake,
    answerId: number,
    query: { after?: Snowflake; limit?: number } = {},
  ): Promise<{ users: APIUser[] }> {
    return this.get(
      `/channels/${channelId}/polls/${messageId}/answers/${answerId}`,
      { query },
    );
  }
  expirePoll(channelId: Snowflake, messageId: Snowflake): Promise<APIMessage> {
    return this.post(`/channels/${channelId}/polls/${messageId}/expire`);
  }

  // --- Threads ------------------------------------------------------------------

  startThreadFromMessage(
    channelId: Snowflake,
    messageId: Snowflake,
    body: StartThreadFromMessageBody,
  ): Promise<APIChannel> {
    return this.post(
      `/channels/${channelId}/messages/${messageId}/threads`,
      body,
    );
  }
  /** Thread sem mensagem, ou post de fórum (com `message` e arquivos). */
  startThread(
    channelId: Snowflake,
    body: StartThreadBody,
    files: UploadFile[] = [],
  ): Promise<APIChannel> {
    return this.http.request('POST', `/chat/channels/${channelId}/threads`, {
      body,
      files,
    });
  }
  joinThread(threadId: Snowflake): Promise<void> {
    return this.put(`/channels/${threadId}/thread-members/@me`);
  }
  leaveThread(threadId: Snowflake): Promise<void> {
    return this.del(`/channels/${threadId}/thread-members/@me`);
  }
  addThreadMember(threadId: Snowflake, userId: Snowflake): Promise<void> {
    return this.put(`/channels/${threadId}/thread-members/${userId}`);
  }
  removeThreadMember(threadId: Snowflake, userId: Snowflake): Promise<void> {
    return this.del(`/channels/${threadId}/thread-members/${userId}`);
  }
  threadMembers(
    threadId: Snowflake,
    query: { with_member?: boolean; after?: Snowflake; limit?: number } = {},
  ): Promise<APIThreadMember[]> {
    return this.get(`/channels/${threadId}/thread-members`, { query });
  }
  /** Posts do fórum (lista do fórum, como o cliente do Discord pede). */
  searchThreads(
    channelId: Snowflake,
    query: ThreadSearchQuery = {},
  ): Promise<ThreadSearchResult> {
    return this.get(`/channels/${channelId}/threads/search`, {
      query: {
        ...query,
        tag: query.tag?.length ? query.tag.join(',') : undefined,
      },
    });
  }
  archivedThreads(
    channelId: Snowflake,
    kind: 'public' | 'private' | 'joined',
    query: { before?: string; limit?: number } = {},
  ): Promise<ThreadList> {
    const path =
      kind === 'joined'
        ? `/channels/${channelId}/users/@me/threads/archived/private`
        : `/channels/${channelId}/threads/archived/${kind}`;
    return this.get(path, { query });
  }

  // --- Grupo ----------------------------------------------------------------------

  addRecipient(channelId: Snowflake, userId: Snowflake): Promise<void> {
    return this.put(`/channels/${channelId}/recipients/${userId}`);
  }
  removeRecipient(channelId: Snowflake, userId: Snowflake): Promise<void> {
    return this.del(`/channels/${channelId}/recipients/${userId}`);
  }
}
